
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface MonetizationModalProps {
  onClose: () => void;
}

export default function MonetizationModal({ onClose }: MonetizationModalProps) {
  const [activeTab, setActiveTab] = useState<'supporters' | 'powerups' | 'ads'>('supporters');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Supporter Tiers State
  const [tiers, setTiers] = useState<any[]>([]);
  const [newTier, setNewTier] = useState({
    tierName: '',
    tierLevel: 1,
    price: '',
    benefits: [''],
    badgeColor: '#8B5CF6'
  });

  // Ad Settings State
  const [adSettings, setAdSettings] = useState({
    adsEnabled: false,
    adFrequency: 'medium',
    estimatedRevenue: 0
  });

  // Earnings State
  const [earnings, setEarnings] = useState({
    supporters: 0,
    powerUps: 0,
    ads: 0,
    total: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load supporter tiers
      const { data: tiersData, error: tiersError } = await supabase
        .from('supporter_tiers')
        .select('*')
        .eq('streamer_id', user.id)
        .order('tier_level');
      
      if (tiersError) throw tiersError;
      if (tiersData) setTiers(tiersData);

      // Load ad settings
      const { data: adData, error: adError } = await supabase
        .from('ad_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (adError && adError.code !== 'PGRST116') { // Ignore "not found" error
        throw adError;
      }
      if (adData) {
        setAdSettings({
          adsEnabled: adData.ads_enabled,
          adFrequency: adData.ad_frequency,
          estimatedRevenue: parseFloat(adData.total_ad_revenue)
        });
      }

      // Load earnings
      const { data: earningsData, error: earningsError } = await supabase
        .from('earnings')
        .select('source_type, amount')
        .eq('user_id', user.id)
        .eq('status', 'completed');
      
      if (earningsError) throw earningsError;
      if (earningsData) {
        const supporterEarnings = earningsData
          .filter(e => e.source_type === 'supporter')
          .reduce((sum, e) => sum + parseFloat(e.amount), 0);
        
        const powerUpEarnings = earningsData
          .filter(e => e.source_type === 'power_up')
          .reduce((sum, e) => sum + parseFloat(e.amount), 0);
        
        const adEarnings = earningsData
          .filter(e => e.source_type === 'ads')
          .reduce((sum, e) => sum + parseFloat(e.amount), 0);

        setEarnings({
          supporters: supporterEarnings,
          powerUps: powerUpEarnings,
          ads: adEarnings,
          total: supporterEarnings + powerUpEarnings + adEarnings
        });
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    }
  };

  const handleCreateTier = async () => {
    if (!newTier.tierName || !newTier.price) return;
    
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-supporter-tier`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tierName: newTier.tierName,
            tierLevel: newTier.tierLevel,
            price: parseFloat(newTier.price),
            benefits: newTier.benefits.filter(b => b.trim()),
            badgeColor: newTier.badgeColor
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create tier');
      }

      await loadData();
      setNewTier({
        tierName: '',
        tierLevel: tiers.length + 1,
        price: '',
        benefits: [''],
        badgeColor: '#8B5CF6'
      });
    } catch (error) {
      console.error('Error creating tier:', error);
      setError('Failed to create tier. Please try again.');
    }
    setLoading(false);
  };

  const handleToggleAds = async () => {
    try {
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const newStatus = !adSettings.adsEnabled;
      
      const { error } = await supabase
        .from('ad_settings')
        .upsert({
          user_id: user.id,
          ads_enabled: newStatus,
          ad_frequency: adSettings.adFrequency,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;
      setAdSettings({ ...adSettings, adsEnabled: newStatus });
    } catch (err) {
      console.error('Error updating ad settings:', err);
      setError('Failed to update ad settings. Please try again.');
    }
  };

  const addBenefitField = () => {
    setNewTier({ ...newTier, benefits: [...newTier.benefits, ''] });
  };

  const updateBenefit = (index: number, value: string) => {
    const updated = [...newTier.benefits];
    updated[index] = value;
    setNewTier({ ...newTier, benefits: updated });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <i className="ri-money-dollar-circle-line text-green-500 mr-3"></i>
            Monetization Dashboard
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-900/50 border border-red-600 text-red-200 text-sm">
            <i className="ri-error-warning-line mr-2"></i>
            {error}
          </div>
        )}

        {/* Earnings Overview */}
        <div className="p-6 bg-gray-900 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Total Earnings</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Supporters</div>
              <div className="text-2xl font-bold text-purple-400">${earnings.supporters.toFixed(2)}</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Power-Ups</div>
              <div className="text-2xl font-bold text-yellow-400">${earnings.powerUps.toFixed(2)}</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Ads</div>
              <div className="text-2xl font-bold text-blue-400">${earnings.ads.toFixed(2)}</div>
            </div>
            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-4">
              <div className="text-white/80 text-sm mb-1">Total</div>
              <div className="text-2xl font-bold text-white">${earnings.total.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('supporters')}
            className={`flex-1 py-4 px-6 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === 'supporters'
                ? 'text-purple-400 border-b-2 border-purple-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <i className="ri-vip-crown-line mr-2"></i>
            Supporter Tiers
          </button>
          <button
            onClick={() => setActiveTab('powerups')}
            className={`flex-1 py-4 px-6 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === 'powerups'
                ? 'text-yellow-400 border-b-2 border-yellow-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <i className="ri-flashlight-line mr-2"></i>
            Power-Ups
          </button>
          <button
            onClick={() => setActiveTab('ads')}
            className={`flex-1 py-4 px-6 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === 'ads'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <i className="ri-advertisement-line mr-2"></i>
            Ad Revenue
          </button>
        </div>

        <div className="p-6">
          {/* Supporter Tiers Tab */}
          {activeTab === 'supporters' && (
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Create Supporter Tiers</h3>
              <p className="text-gray-400 mb-6">
                Set up monthly subscription tiers for your fans. They get exclusive perks, you get recurring income!
              </p>

              <div className="bg-gray-900 rounded-lg p-6 mb-6">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-white font-semibold mb-2">Tier Name</label>
                    <input
                      type="text"
                      value={newTier.tierName}
                      onChange={(e) => setNewTier({ ...newTier, tierName: e.target.value })}
                      placeholder="e.g., Elite Supporter"
                      className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-purple-600 focus:outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2">Monthly Price</label>
                    <input
                      type="number"
                      value={newTier.price}
                      onChange={(e) => setNewTier({ ...newTier, price: e.target.value })}
                      placeholder="9.99"
                      step="0.01"
                      className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-purple-600 focus:outline-none text-sm"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-white font-semibold mb-2">Benefits</label>
                  {newTier.benefits.map((benefit, index) => (
                    <input
                      key={index}
                      type="text"
                      value={benefit}
                      onChange={(e) => updateBenefit(index, e.target.value)}
                      placeholder="e.g., Exclusive badge, Priority chat"
                      className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-purple-600 focus:outline-none text-sm mb-2"
                    />
                  ))}
                  <button
                    onClick={addBenefitField}
                    className="text-purple-400 hover:text-purple-300 text-sm font-semibold cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line mr-1"></i>
                    Add Benefit
                  </button>
                </div>

                <button
                  onClick={handleCreateTier}
                  disabled={loading || !newTier.tierName || !newTier.price}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Tier'}
                </button>
              </div>

              {/* Existing Tiers */}
              {tiers.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-white mb-4">Your Tiers</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {tiers.map((tier) => (
                      <div key={tier.id} className="bg-gray-900 rounded-lg p-4 border-2 border-purple-600/30">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-lg font-bold text-white">{tier.tier_name}</h5>
                          <span className="text-xl font-bold text-purple-400">${tier.price}/mo</span>
                        </div>
                        <ul className="space-y-2">
                          {tier.benefits.map((benefit: string, idx: number) => (
                            <li key={idx} className="text-gray-300 text-sm flex items-start">
                              <i className="ri-check-line text-purple-400 mr-2 mt-0.5"></i>
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Power-Ups Tab */}
          {activeTab === 'powerups' && (
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Power-Up Settings</h3>
              <p className="text-gray-400 mb-6">
                Power-Ups are one-time tips from your fans. They can send you support with custom messages!
              </p>

              <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 border border-yellow-600/30 rounded-lg p-6 mb-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-flashlight-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-2">How Power-Ups Work</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• Fans can send you any amount ($1 minimum)</li>
                      <li>• They can include a custom message</li>
                      <li>• You keep 85% (15% platform fee)</li>
                      <li>• Power-Ups appear in your stream chat</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-white mb-4">Suggested Amounts</h4>
                <div className="grid grid-cols-3 gap-3">
                  {[5, 10, 25, 50, 100, 250].map((amount) => (
                    <div key={amount} className="bg-gray-800 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-400">${amount}</div>
                      <div className="text-gray-400 text-sm mt-1">You get ${(amount * 0.85).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Ads Tab */}
          {activeTab === 'ads' && (
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Ad Revenue Settings</h3>
              <p className="text-gray-400 mb-6">
                Enable ads on your streams and videos to earn passive income. You control when and how often ads appear.
              </p>

              <div className="bg-gray-900 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">Enable Ads</h4>
                    <p className="text-gray-400 text-sm">Show ads during your streams and videos</p>
                  </div>
                  <button
                    onClick={handleToggleAds}
                    className={`relative w-16 h-8 rounded-full transition-colors cursor-pointer ${
                      adSettings.adsEnabled ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <div
                      className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                        adSettings.adsEnabled ? 'transform translate-x-8' : ''
                      }`}
                    ></div>
                  </button>
                </div>

                {adSettings.adsEnabled && (
                  <div>
                    <label className="block text-white font-semibold mb-3">Ad Frequency</label>
                    <div className="grid grid-cols-3 gap-3">
                      {['low', 'medium', 'high'].map((freq) => (
                        <button
                          key={freq}
                          onClick={() => setAdSettings({ ...adSettings, adFrequency: freq })}
                          className={`py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                            adSettings.adFrequency === freq
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-800 text-gray-400 border border-gray-700'
                          }`}
                        >
                          {freq.charAt(0).toUpperCase() + freq.slice(1)}
                        </button>
                      ))}
                    </div>
                    <p className="text-gray-400 text-sm mt-3">
                      {adSettings.adFrequency === 'low' && 'Ads every 30 minutes • Less intrusive'}
                      {adSettings.adFrequency === 'medium' && 'Ads every 15 minutes • Balanced'}
                      {adSettings.adFrequency === 'high' && 'Ads every 10 minutes • Maximum revenue'}
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-blue-600/30 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-white mb-4">Estimated Earnings</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-gray-900/50 rounded-lg p-4">
                    <div className="text-gray-400 text-sm mb-1">Per 1,000 Views</div>
                    <div className="text-2xl font-bold text-blue-400">$2.50</div>
                  </div>
                  <div className="bg-gray-900/50 rounded-lg p-4">
                    <div className="text-gray-400 text-sm mb-1">This Month</div>
                    <div className="text-2xl font-bold text-blue-400">${adSettings.estimatedRevenue.toFixed(2)}</div>
                  </div>
                  <div className="bg-gray-900/50 rounded-lg p-4">
                    <div className="text-gray-400 text-sm mb-1">Projected Monthly</div>
                    <div className="text-2xl font-bold text-blue-400">$125.00</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
